import { useEffect, useState } from 'react';

import { PlayerComparisonCard } from './PlayerComparisonCard';
import { TransfersCard } from './TransfersCard';
import { TransferHitModal } from '@components/pick/TransferHitModal';
import { useCustomTransferSuggestions } from '@hooks/transfers/useCustomTransferSuggestions';
import { useMakeTransfers } from '@hooks/transfers/useMakeTransfers';
import { useCustomTransfersStore } from '@lib/transfers/store/useCustomTransfersStore';
import {
  deriveTransfersViewState,
  isTransfersVisible
} from '@lib/transfers/utils/transfers-view-state';

export const CustomTransferSuggestions = () => {
  const dismissSuggestions = useCustomTransfersStore(
    (state) => state.dismissSuggestions
  );
  const commitRegenerate = useCustomTransfersStore(
    (state) => state.commitRegenerate
  );
  const resetCustomTransfersLocalStore = useCustomTransfersStore(
    (state) => state.resetCustomTransfersLocalStore
  );

  const {
    transfers,
    isLoading,
    isError,
    refusalDescription,
    hasLoaded,
    freeTransfers,
    remainingTransfers,
    transfersMade,
    isAllowanceKnown,
    isUnlimited
  } = useCustomTransferSuggestions();

  // A commit invalidates the filters along with the suggestions they produced:
  // the squad they were solved against no longer exists, so the tab starts
  // clean rather than inviting a re-run of a stale set.
  const {
    makeTransfers,
    isMakingTransfers,
    transferHit,
    isHitWarningOpen,
    confirmHit,
    dismissHit
  } = useMakeTransfers({
    transfers,
    // Null means "no hit is possible": unlimited by chip, or an allowance we
    // could not read, which must not be priced as though it were zero.
    freeTransfers: isUnlimited || !isAllowanceKnown ? null : freeTransfers,
    transfersAlreadyMade: transfersMade,
    onCommitted: resetCustomTransfersLocalStore
  });

  // Which transfer the comparison card is inspecting; rows in the transfers
  // list drive it. Reset whenever a new suggestion set lands so the card
  // can't point at a stale index.
  const [selectedIndex, setSelectedIndex] = useState(0);
  useEffect(() => {
    setSelectedIndex(0);
  }, [transfers]);

  const viewState = deriveTransfersViewState({
    isLoading,
    isError,
    hasLoaded,
    hasTransfers: transfers.length > 0,
    freeTransfers,
    remainingTransfers,
    isUnlimited,
    mode: 'custom'
  });

  return (
    <div className="flex flex-col gap-5" data-cy="custom-transfer-suggestions">
      <TransfersCard
        mode="custom"
        transfers={transfers}
        isLoading={isLoading}
        isError={isError}
        refusalDescription={refusalDescription}
        hasLoaded={hasLoaded}
        freeTransfers={freeTransfers}
        remainingTransfers={remainingTransfers}
        isUnlimited={isUnlimited}
        selectedIndex={selectedIndex}
        onSelect={setSelectedIndex}
        onCommitRegenerate={commitRegenerate}
        onMakeTransfers={makeTransfers}
        isMakingTransfers={isMakingTransfers}
        onDismiss={dismissSuggestions}
      />
      {isTransfersVisible(viewState) && (
        <PlayerComparisonCard
          transfers={transfers}
          selectedIndex={selectedIndex}
        />
      )}
      <TransferHitModal
        isOpen={isHitWarningOpen}
        hit={transferHit}
        onConfirm={confirmHit}
        onDismiss={dismissHit}
        isConfirming={isMakingTransfers}
      />
    </div>
  );
};
